import React, { useCallback, useEffect, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useHistory, useParams } from 'react-router-dom';

import { Box, Container } from '@mui/material';

import Spinner from '~/components/Common/Spinner';

import useCurrentUser from '~/hooks/useCurrentUser';

import CorporateEventsService, {
  CreateEventParams,
  InvitedUser,
  RegistrationCustomQuestion,
} from '~/services/CorporateEventsService';

import EventForm from '../components/EventForm';
import EventFormHeader from '../components/EventFormHeader';
import InvitedUsers from '../components/InvitedUsers/InvitedUsers';
import RegistrationCustomQuestions from '../components/RegistrationCustomQuestions/RegistrationCustomQuestions';

export default function EventFormPage() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tab, setTab] = useState('details');
  const [customQuestions, setCustomQuestions] = useState<Array<RegistrationCustomQuestion>>([]);
  const [invitedUsers, setInvitedUsers] = useState<Array<InvitedUser>>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const isEditMode = !!id;

  const { data: event, isLoading } = useQuery({
    queryKey: ['corporate-events', 'events', id],
    queryFn: () => CorporateEventsService.getEventById(id),
    enabled: isEditMode,
  });

  const { data: questions = [] } = useQuery({
    queryKey: ['corporate-events', 'registration-custom-questions', id],
    queryFn: () => CorporateEventsService.getRegistrationCustomQuestionsByEventId(id),
    enabled: isEditMode && !!id,
  });

  const { data: invitedUsersData = [] } = useQuery({
    queryKey: ['corporate-events', 'invited-users', id],
    queryFn: () => CorporateEventsService.getInvitedUsersByEventId(id),
    enabled: isEditMode && !!id,
  });

  useEffect(() => {
    if (isEditMode && questions.length > 0) {
      setCustomQuestions(questions);
    }
  }, [isEditMode, questions]);

  useEffect(() => {
    if (isEditMode && invitedUsersData.length > 0) {
      setInvitedUsers(invitedUsersData);
    }
  }, [isEditMode, invitedUsersData]);

  const createMutation = useMutation({
    mutationFn: (data: CreateEventParams) => CorporateEventsService.createEvent(data),
    onSuccess: async (createdEvent) => {
      queryClient.invalidateQueries({ queryKey: ['corporate-events', 'events'] });

      if (createdEvent.id) {
        // Save custom questions if any
        if (customQuestions.length > 0) {
          try {
            for (const question of customQuestions) {
              const questionData = {
                eventId: createdEvent.id,
                label: question.label,
                description: question.description,
                fieldName: question.fieldName,
                fieldType: question.fieldType,
                isRequired: question.isRequired,
                sortOrder: question.sortOrder,
                isSensitive: question.isSensitive,
              };
              const createdQuestion = await CorporateEventsService.createRegistrationCustomQuestion(questionData);

              if (question.options && question.options.length > 0) {
                for (const option of question.options) {
                  await CorporateEventsService.createRegistrationCustomQuestionOption({
                    questionId: createdQuestion.id,
                    label: option.label,
                    value: option.value,
                    sortOrder: option.sortOrder,
                  });
                }
              }
            }
          } catch (error) {
            enqueueSnackbar(`Event created but failed to save some custom questions: ${(error as Error).message}`, {
              variant: 'warning',
            });
          }
        }

        if (invitedUsers.length > 0) {
          try {
            for (const user of invitedUsers) {
              await CorporateEventsService.createInvitedUser(createdEvent.id, {
                fullName: user.fullName,
                email: user.email,
                department: user.department,
                role: user.role,
                notes: user.notes,
                hasAnalyticsAccess: user.hasAnalyticsAccess,
              });
            }
          } catch (error) {
            enqueueSnackbar(`Event created but failed to save some invited users: ${(error as Error).message}`, {
              variant: 'warning',
            });
          }
        }

        history.push(`/corporate-events/events/${createdEvent.id}/edit`);
        enqueueSnackbar('Event created successfully', { variant: 'success' });
      }
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error creating event: ${error.message}`, { variant: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateEventParams }) => CorporateEventsService.updateEvent(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['corporate-events', 'events'] });
      queryClient.invalidateQueries({ queryKey: ['corporate-events', 'events', id] });
      enqueueSnackbar('Event updated successfully', { variant: 'success' });
      setHasUnsavedChanges(false);
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error updating event: ${error.message}`, { variant: 'error' });
    },
  });

  const handleSubmit = useCallback(
    async (data: CreateEventParams) => {
      setIsSubmitting(true);
      try {
        if (isEditMode && id) {
          const updateData = { ...data };
          delete updateData.createdBy;
          await updateMutation.mutateAsync({ id, data: updateData });
        } else {
          const eventData: CreateEventParams = {
            ...data,
            createdBy: user?.id_member || '',
          };
          await createMutation.mutateAsync(eventData);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [isEditMode, id, createMutation, updateMutation, user],
  );

  const handleCancel = () => {
    history.push('/corporate-events/events');
  };

  if (isEditMode && isLoading) {
    return (
      <Container maxWidth="xl">
        <Spinner />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ pt: 3 }}>
      <Helmet>
        <title>{isEditMode ? 'Edit Event' : 'Create Event'}</title>
      </Helmet>

      <EventFormHeader
        tab={tab}
        event={event}
        onBack={handleCancel}
        onTabChange={setTab}
        hasUnsavedChanges={hasUnsavedChanges}
        isCreateMode={!isEditMode}
      />

      <Box mt={3}>
        {tab === 'details' && (
          <EventForm
            event={event}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting || createMutation.isPending || updateMutation.isPending}
            onHasUnsavedChangesChange={setHasUnsavedChanges}
          />
        )}
        {tab === 'custom-questions' && (
          <RegistrationCustomQuestions
            eventId={event?.id}
            questions={customQuestions}
            onQuestionsChange={setCustomQuestions}
          />
        )}
        {tab === 'invited-users' && (
          <InvitedUsers eventId={event?.id} invitedUsers={invitedUsers} onInvitedUsersChange={setInvitedUsers} />
        )}
      </Box>
    </Container>
  );
}
